import { Image, Tooltip } from 'components/ui'
import { useTheme } from 'next-themes'
import { cn } from 'utils/cn'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { type MouseEvent, type ReactNode } from 'react'
import { ChevronLeft, ChevronRight } from '@carbon/icons-react'
import { useMenuPreferences } from '../../../hooks/useMenuPreferences'
import ThemeToggle from '../ThemeToggle'

export interface NavItem {
  label: string
  path: string
  icon: React.ComponentType<{ size?: number | string; color?: string }>
  external?: boolean
  notificationCount?: number
  /** Additional routes that should highlight this nav item as active */
  activePaths?: string[]
}

export interface DividerItem {
  type: 'divider'
}

export type SidebarNavItem = NavItem | DividerItem

function isDividerItem(item: SidebarNavItem): item is DividerItem {
  return 'type' in item && item.type === 'divider'
}

export interface SidebarProps {
  navItems: SidebarNavItem[]
  bottomItems: NavItem[]
  logoLink: string
  logoSrc?: string
  iconSrc?: string
  headerContent?: ReactNode
  topNavContent?: ReactNode
  additionalNavContent?: ReactNode
  useMenuPreferencesHook?: boolean
  isCollapsed?: boolean
  onToggleCollapse?: () => void
  isDrawerMode?: boolean
  onNavigate?: () => void
}

const toggleButtonClass =
  'flex h-8 w-8 min-w-[32px] items-center justify-center rounded-full border border-line-subtle bg-surface-muted p-0 text-content-secondary transition-colors hover:bg-surface-subtle hover:text-content-primary'

export const Sidebar: React.FC<SidebarProps> = ({
  navItems,
  bottomItems,
  logoLink,
  logoSrc: logoSrcProp,
  iconSrc: iconSrcProp,
  headerContent,
  topNavContent,
  additionalNavContent,
  useMenuPreferencesHook = true,
  isCollapsed: isCollapsedProp,
  onToggleCollapse,
  isDrawerMode = false,
  onNavigate,
}) => {
  const router = useRouter()
  const { isCollapsed: isCollapsedFromHook, toggleMenu } = useMenuPreferences()

  const isCollapsed = isDrawerMode
    ? false
    : useMenuPreferencesHook
      ? isCollapsedFromHook
      : (isCollapsedProp ?? false)
  const toggleMenuFn = useMenuPreferencesHook ? toggleMenu : (onToggleCollapse ?? (() => {}))
  const menuWidth = isDrawerMode ? 260 : isCollapsed ? 70 : 200

  const { resolvedTheme } = useTheme()
  const isDark = resolvedTheme === 'dark'
  const defaultLogoSrc = isDark ? '/logo-white.svg' : '/logo-black.svg'
  const defaultIconSrc = isDark ? '/icon-white.svg' : '/icon-black.svg'
  const logoSrc = logoSrcProp ?? defaultLogoSrc
  const iconSrc = iconSrcProp ?? defaultIconSrc

  const renderNavLabel = (label: string, active = false) => (
    <>
      <p
        className={cn(
          active ? 'text-brand-accent-strong dark:text-brand-accent-200' : 'text-content-secondary',
          cn(
            'overflow-hidden whitespace-nowrap text-sm transition-[opacity,max-width,margin] duration-300 ease-in-out',
            isCollapsed ? 'ml-0 max-w-0 opacity-0' : 'ml-3 max-w-[180px] opacity-100',
          ),
        )}
      >
        {label}
      </p>
    </>
  )

  return (
    <div
      className={cn(
        'flex h-screen min-h-screen flex-col overflow-hidden bg-surface-subtle px-0 pb-0 pt-8',
        isDrawerMode
          ? 'relative w-full'
          : 'fixed left-0 top-0 z-10 border-r border-line-subtle transition-[width,padding] duration-300 ease-in-out',
      )}
      style={isDrawerMode ? undefined : { width: `${menuWidth}px` }}
      role="group"
    >
      {/* Header */}
      <div
        className={cn(
          'mb-4 flex-shrink-0',
          isDrawerMode ? 'text-left' : 'text-center',
          isCollapsed ? 'px-2' : 'px-4',
        )}
      >
        <Link href={logoLink} style={{ textDecoration: 'none' }}>
          <Image
            src={isCollapsed ? iconSrc : logoSrc}
            className={cn('mb-2', isDrawerMode ? '' : 'mx-auto')}
            style={{ width: isCollapsed ? '32px' : '150px' }}
            alt="SolvaPay"
          />
        </Link>
        {headerContent ? (
          headerContent
        ) : !isCollapsed ? (
          <div className={cn('flex flex-row items-center', 'mt-3 justify-center gap-3')}>
            <div className="flex h-8 w-8 items-center justify-center rounded-full border border-line-subtle bg-surface-muted">
              <ThemeToggle />
            </div>
            <Tooltip label="Collapse menu" placement="bottom" openDelay={300}>
              <button
                type="button"
                aria-label="Collapse menu"
                onClick={toggleMenuFn}
                className={toggleButtonClass}
              >
                <ChevronLeft size={16} />
              </button>
            </Tooltip>
          </div>
        ) : (
          <div className={cn('flex flex-col items-center', 'mt-3 gap-2')}>
            <div className="flex h-8 w-8 items-center justify-center rounded-full border border-line-subtle bg-surface-muted">
              <ThemeToggle />
            </div>
            <Tooltip label="Expand menu" placement="right" openDelay={300}>
              <button
                type="button"
                aria-label="Expand menu"
                onClick={toggleMenuFn}
                className={toggleButtonClass}
              >
                <ChevronRight size={16} />
              </button>
            </Tooltip>
          </div>
        )}
      </div>
      {/* Main Navigation - Scrollable area */}
      <div
        className={cn(
          'flex flex-col items-center',
          'min-h-0 flex-1 items-stretch gap-0.5 overflow-y-auto overflow-x-hidden pb-4 [&>*]:flex-shrink-0',
        )}
      >
        {topNavContent}
        {navItems.map((item, index) => {
          // Handle dividers
          if (isDividerItem(item)) {
            return (
              <div
                 
                key={`divider-${index}`}
                className={cn('flex w-full items-center py-1.5', isCollapsed ? 'px-2' : 'px-4')}
              >
                <div
                  className={cn(
                    'h-px flex-1 bg-gray-300 transition-opacity duration-200 ease-in-out dark:bg-gray-600',
                    isCollapsed ? 'opacity-0' : 'opacity-100',
                  )}
                />
              </div>
            )
          }

          // Type guard: after divider check, item must be NavItem
          const navItem = item as NavItem
          if (!navItem.icon || !navItem.path) {
            return null
          }

          const Icon = navItem.icon
          const active = [navItem.path, ...(navItem.activePaths ?? [])].some(path =>
            router.asPath.startsWith(path),
          )
          const notificationCount = navItem.notificationCount ?? 0

          return (
            <Tooltip
              label={
                notificationCount > 0
                  ? `${navItem.label} (${notificationCount})`
                  : isCollapsed
                    ? navItem.label
                    : undefined
              }
              placement="right"
              key={navItem.path}
              openDelay={300}
            >
              <div
                className={cn(
                  'relative flex min-h-[44px] w-full cursor-pointer flex-col justify-center px-4 py-2 no-underline transition-[background,color,padding] duration-100 ease-in-out',
                  isCollapsed ? 'items-center' : 'items-stretch',
                  active
                    ? 'rounded-none bg-brand-accent-100 font-semibold text-brand-accent-strong dark:bg-brand-accent-900 dark:text-brand-accent-200'
                    : 'rounded-md bg-transparent font-normal text-content-secondary',
                )}
                onClick={(e: MouseEvent<HTMLDivElement>) => {
                  e.preventDefault()
                  e.stopPropagation()
                  if (navItem.external) {
                    window.open(navItem.path, '_blank', 'noopener,noreferrer')
                  } else {
                    router.push(navItem.path)
                    onNavigate?.()
                  }
                }}
              >
                <div className={cn('flex w-full items-center', isCollapsed && 'justify-center')}>
                  <div
                    className={cn(
                      'relative flex h-4 min-w-[16px] items-center justify-center',
                      active ? 'text-brand-orange' : 'text-content-secondary',
                    )}
                  >
                    <Icon size={16} color="currentColor" />
                    {notificationCount > 0 && (
                      <div
                        aria-hidden
                        className="absolute -right-[3px] -top-[3px] h-2 w-2 rounded-full border-2 border-surface-subtle bg-danger"
                      />
                    )}
                  </div>
                  {renderNavLabel(navItem.label, active)}
                </div>
              </div>
            </Tooltip>
          )
        })}
        {additionalNavContent}
      </div>
      {/* Bottom Items - Anchored to viewport bottom */}
      <div className="mt-auto flex-shrink-0 border-t border-line-subtle bg-surface-subtle px-0 pb-0 pt-2">
        <div className={cn('flex flex-col items-center', 'items-stretch gap-0.5')}>
          {bottomItems.map(item => {
            const Icon = item.icon
            const itemClass = cn(
              'relative flex min-h-[44px] flex-col justify-center rounded-md px-4 py-2 text-content-secondary no-underline transition-[background,color,padding] duration-100 ease-in-out hover:bg-surface-subtle hover:text-content-primary',
              isCollapsed ? 'items-center' : 'items-stretch',
            )
            const inner = (
              <>
                <div className="flex items-center">
                  <div className="flex h-4 min-w-[16px] items-center justify-center text-content-secondary">
                    <Icon size={16} color="currentColor" />
                  </div>
                  {renderNavLabel(item.label)}
                </div>
              </>
            )
            return (
              <Tooltip
                label={isCollapsed ? item.label : undefined}
                placement="right"
                key={item.path}
                openDelay={300}
              >
                {item.external ? (
                  <a
                    href={item.path}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={itemClass}
                  >
                    {inner}
                  </a>
                ) : (
                  <Link href={item.path} className={itemClass}>
                    {inner}
                  </Link>
                )}
              </Tooltip>
            )
          })}
        </div>
      </div>
    </div>
  )
}

export default Sidebar
